import {
  genderList,
  individualOrBusinessList,
  IndOrBus,
  Lodgement,
  policyClaimList,
  titleList,
  YesNoList,
} from 'contexts/FormProvider';
import * as yup from 'yup';

import countries from 'resources/addresses';
import { validateDateSchema, validateTextSchema, validatePostCode, textAreaRegex } from 'utils/commonSchema';
import { textFieldRegex } from 'utils/commonSchema';
const emailRegex =
  /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
const phoneNumberRegex =
  // eslint-disable-next-line no-useless-escape
  /^(([0-9]{6,20})|([\+]?[(]?(([2-9][0-9])|([2-8][0-9][0-9]))[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}))$/;
const textFieldMax = 50;
const textFieldMin = 4;
const phoneNumberMax = 20;
const emailMax = 100;
const postCodeRegexNSW = /^2[0-9][0-9][0-9]$/;
const postCodeRegexACT = /^2[0-9][0-9][0-9]$/;
const postCodeRegexVIC = /^3[0-9][0-9][0-9]$/;
const postCodeRegexQLD = /^4[0-9][0-9][0-9]$/;
const postCodeRegexSA = /^5[0-9][0-9][0-9]$/;
const postCodeRegexWA = /^6[0-9][0-9][0-9]$/;
const postCodeRegexTAS = /^7[0-9][0-9][0-9]$/;
const postCodeRegexNT = /^0[0-9][0-9][0-9]$/;

const postalAddressErrorMsgMin = 'Remember to include your unit number as well as your street number';
const textFieldNameMin = 1;
const otherLanguageMin = 3;
const addressMax = 225;
const stateMax = 20;

export const contactReasonSchemaObject = {
  policyOrClaim: yup.string().when('lodgement', {
    is: 'Complaint',
    then: () => yup.string().oneOf([...policyClaimList]),
    otherwise: () => yup.string().oneOf([...policyClaimList, 'other']),
  }),

  policyNumber: yup
    .string()
    .nullable()
    .when('policyOrClaim', {
      is: 'policy',
      then: () =>
        yup
          .string()
          .matches(textFieldRegex, 'Please enter alpha numeric characters only')
          .min(textFieldMin, 'Please enter 4 characters or more')
          .max(textFieldMax)
          .required('Please enter 4 characters or more'),
    }),

  claimNumber: yup
    .string()
    .nullable()
    .when('policyOrClaim', {
      is: 'claim',
      then: () =>
        yup
          .string()
          .matches(textFieldRegex, 'Please enter alpha numeric characters only')
          .min(textFieldMin, 'Please enter 4 characters or more')
          .max(textFieldMax)
          .required('Please enter 4 characters or more'),
    }),

  indOrBus: yup
    .string()
    .ensure()
    .oneOf([...individualOrBusinessList])
    .required(),

  businessName: yup
    .string()
    .nullable()
    .when('indOrBus', {
      is: 'business',
      then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: textFieldMax, required: true }),
    }),

  isSmallBusiness: yup
    .string()
    .nullable()
    .when(['indOrBus', 'lodgement'], {
      is: (indOrBus: IndOrBus, lodgement: Lodgement) => indOrBus === 'business' && lodgement === 'Complaint',
      then: () =>
        yup
          .string()
          .oneOf([...YesNoList])
          .required(),
    }),
};

export const phoneNumberSchema = (excludeEmptyString = true) =>
  yup
    .string()
    .matches(phoneNumberRegex, { excludeEmptyString, message: 'Please enter a valid phone number' })
    .max(phoneNumberMax);

export const emailSchema = ({ excludeEmptyString = true, required = true }) => {
  const schema = yup
    .string()
    .matches(emailRegex, { excludeEmptyString, message: 'Please enter a valid email address' })
    .max(emailMax);

  return required ? schema.required('Please enter a valid email address') : schema;
};

const contactDetailSchemaObject = {
  iContactPreferedMethod: yup.string().when('lodgement', {
    is: 'Complaint',
    then: () => yup.string().oneOf(['email', 'post', 'phone']).required(),
  }),

  addressType: yup.string().when(['lodgement'], {
    is: (lodgement: Lodgement) => lodgement === 'Complaint',
    then: () =>
      yup
        .string()
        .oneOf([...YesNoList])
        .nullable()
        .required(),
    otherwise: () => yup.string().nullable(),
  }),

  cEmailAddress: yup.string().when('iContactPreferedMethod', {
    is: 'email',
    then: () => emailSchema({ excludeEmptyString: false }),
    otherwise: () => emailSchema({ excludeEmptyString: true, required: false }),
  }),

  physicalAddress: yup
    .string()
    .when(['lodgement', 'isManualPhysAddress'], {
      is: (lodgement: Lodgement, isManualPhysAddress: boolean) => lodgement === 'Complaint' && !isManualPhysAddress,
      then: () => yup.string().required(postalAddressErrorMsgMin).min(4, postalAddressErrorMsgMin),
    })
    .when(['isManualPhysAddress', 'lodgement'], {
      is: (isManualPhysAddress: boolean, lodgement: Lodgement) => !isManualPhysAddress && lodgement !== 'Complaint',
      then: () => yup.string().nullable(),
    }),

  isPhysAddressValid: yup
    .boolean()
    .when(['lodgement', 'isManualPhysAddress'], {
      is: (lodgement: Lodgement, isManualPhysAddress: boolean) => lodgement === 'Complaint' && !isManualPhysAddress,
      then: () => yup.boolean().required().oneOf([true]),
    })
    .when(['isManualPhysAddress', 'lodgement'], {
      is: (isManualPhysAddress: boolean, lodgement: Lodgement) => !isManualPhysAddress && lodgement !== 'Complaint',
      then: () => yup.boolean(),
    }),

  phyStreetAddress: yup
    .string()
    .when(['isManualPhysAddress', 'lodgement'], {
      is: (isManualPhysAddress: boolean, lodgement: Lodgement) => isManualPhysAddress && lodgement === 'Complaint',
      then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: addressMax, required: true }),
    })
    .test('streetAddress', 'Please enter a valid value', function (value) {
      const { isManualPhysAddress, phySuburb, phyState, phyPostcode, lodgement, cEmailAddress } = this.parent;

      if (isManualPhysAddress && lodgement !== 'Complaint') {
        if (phySuburb || phyState || phyPostcode || !cEmailAddress) {
          const schema = validateTextSchema({ minLen: textFieldNameMin, maxLen: addressMax, required: true });
          return schema.isValidSync(value);
        }
      }

      const schema = yup.string();
      return schema.isValidSync(value);
    }),

  phySuburb: yup
    .string()
    .when(['isManualPhysAddress', 'lodgement'], {
      is: (isManualPhysAddress: boolean, lodgement: Lodgement) => isManualPhysAddress && lodgement === 'Complaint',
      then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: addressMax, required: true }),
    })
    .test('suburb', 'Please enter a valid value', function (value) {
      const { isManualPhysAddress, phyStreetAddress, phyState, phyPostcode, lodgement, cEmailAddress } = this.parent;

      if (isManualPhysAddress && lodgement !== 'Complaint') {
        if (phyStreetAddress || phyState || phyPostcode || !cEmailAddress) {
          const schema = validateTextSchema({ minLen: textFieldNameMin, maxLen: addressMax, required: true });
          return schema.isValidSync(value);
        }
      }

      const schema = yup.string();
      return schema.isValidSync(value);
    }),

  phyState: yup
    .string()
    .when(['isManualPhysAddress', 'lodgement'], {
      is: (isManualPhysAddress: boolean, lodgement: Lodgement) => isManualPhysAddress && lodgement === 'Complaint',
      then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: stateMax, required: true }),
    })
    .test('state', 'Please enter a valid value', function (value) {
      const { isManualPhysAddress, phyStreetAddress, phySuburb, phyPostcode, lodgement, cEmailAddress } = this.parent;

      if (isManualPhysAddress && lodgement !== 'Complaint') {
        if (phyStreetAddress || phySuburb || phyPostcode || !cEmailAddress) {
          const schema = validateTextSchema({ minLen: textFieldNameMin, maxLen: stateMax, required: true });
          return schema.isValidSync(value);
        }
      }

      const schema = yup.string();
      return schema.isValidSync(value);
    }),

  phyCountry: yup
    .string()
    .nullable()
    .when(['isManualPhysAddress', 'lodgement'], {
      is: (isManualPhysAddress: boolean, lodgement: Lodgement) => isManualPhysAddress && lodgement === 'Complaint',
      then: () => yup.string().oneOf(countries, 'Country is a required field').required().label('Country'),
    })
    .test('country', 'Country is a required field', function (value) {
      const { isManualPhysAddress, phyStreetAddress, phySuburb, phyState, phyPostcode, lodgement, cEmailAddress } =
        this.parent;

      if (isManualPhysAddress && lodgement !== 'Complaint') {
        if (phyStreetAddress || phySuburb || phyPostcode || phyState || !cEmailAddress) {
          const schema = yup.string().oneOf(countries).required().label('Country');
          return schema.isValidSync(value);
        }
      }

      const schema = yup.string().nullable();
      return schema.isValidSync(value);
    }),

  phyPostcode: yup.string().test('postalCode', 'Invalid postcode', function (value) {
    const { isManualPhysAddress, phyStreetAddress, phySuburb, phyState, phyCountry, lodgement, cEmailAddress } =
      this.parent;

    const isEmailValid = emailSchema({ excludeEmptyString: false, required: false }).isValidSync(cEmailAddress);

    const isStateValid = (value: string) =>
      phyState === value && lodgement !== 'Complaint' && (phyStreetAddress || phySuburb || phyState || !cEmailAddress);

    if (isManualPhysAddress) {
      // Scenario(Non-Complaint): phyState is ACT and there is at least one value for phyStreetAddress, phySuburb, phyState or no email address.
      if (phyCountry === 'Australia' && (phyState === 'ACT' || isStateValid('ACT'))) {
        const schema = yup
          .string()
          .required()
          .matches(postCodeRegexACT || /^2620$/, 'Invalid postcode')
          .min(4)
          .max(4, 'Invalid postcode');
        return schema.isValidSync(value);
      }

      // Scenario(Non-Complaint): phyState is NSW and there is at least one value for phyStreetAddress, phySuburb, phyState or no email address.
      if (phyCountry === 'Australia' && (phyState === 'NSW' || isStateValid('NSW'))) {
        const schemaNSW = yup
          .string()
          .required()
          .matches(postCodeRegexNSW, 'Invalid postcode')
          .min(4)
          .max(4, 'Invalid postcode');

        const schemaNSW1 = yup
          .string()
          .required()
          .matches(/^4375$/, 'Invalid postcode')
          .min(4)
          .max(4, 'Invalid postcode');

        const schemaNSW2 = yup
          .string()
          .required()
          .matches(/^4380$/, 'Invalid postcode')
          .min(4)
          .max(4, 'Invalid postcode');

        const schemaNSW3 = yup
          .string()
          .required()
          .matches(/^4385$/, 'Invalid postcode')
          .min(4)
          .max(4, 'Invalid postcode');

        return (
          schemaNSW.isValidSync(value) ||
          schemaNSW1.isValidSync(value) ||
          schemaNSW2.isValidSync(value) ||
          schemaNSW3.isValidSync(value)
        );
      }

      // Scenario(Non-Complaint): phyState is VIC and there is at least one value for phyStreetAddress, phySuburb, phyState or no email address.
      if (phyCountry === 'Australia' && (phyState === 'VIC' || isStateValid('VIC'))) {
        const schema = yup
          .string()
          .required()
          .matches(postCodeRegexVIC, 'Invalid postcode')
          .min(4)
          .max(4, 'Invalid postcode');
        return schema.isValidSync(value);
      }

      // Scenario(Non-Complaint): phyState is QLD and there is at least one value for phyStreetAddress, phySuburb, phyState or no email address.
      if (phyCountry === 'Australia' && (phyState === 'QLD' || isStateValid('QLD'))) {
        const schema = yup
          .string()
          .required()
          .matches(postCodeRegexQLD, 'Invalid postcode')
          .min(4)
          .max(4, 'Invalid postcode');
        const schemaQLD = yup
          .string()
          .required()
          .matches(/^2460$/, 'Invalid postcode')
          .min(4)
          .max(4, 'Invalid postcode');
        return schema.isValidSync(value) || schemaQLD.isValidSync(value);
      }

      // Scenario(Non-Complaint): phyState is SA and there is at least one value for phyStreetAddress, phySuburb, phyState or no email address.
      if (phyCountry === 'Australia' && (phyState === 'SA' || isStateValid('SA'))) {
        const schema = yup
          .string()
          .required()
          .matches(postCodeRegexSA, 'Invalid postcode')
          .min(4)
          .max(4, 'Invalid postcode');
        return schema.isValidSync(value);
      }

      // Scenario(Non-Complaint): phyState is WA and there is at least one value for phyStreetAddress, phySuburb, phyState or no email address.
      if (phyCountry === 'Australia' && (phyState === 'WA' || isStateValid('WA'))) {
        const schema = yup
          .string()
          .required()
          .matches(postCodeRegexWA, 'Invalid postcode')
          .min(4)
          .max(4, 'Invalid postcode');
        return schema.isValidSync(value);
      }

      // Scenario(Non-Complaint): phyState is TAS and there is at least one value for phyStreetAddress, phySuburb, phyState or no email address.
      if (phyCountry === 'Australia' && (phyState === 'TAS' || isStateValid('TAS'))) {
        const schema = yup
          .string()
          .required()
          .matches(postCodeRegexTAS, 'Invalid postcode')
          .min(4)
          .max(4, 'Invalid postcode');
        return schema.isValidSync(value);
      }

      // Scenario(Non-Complaint): phyState is NT and there is at least one value for phyStreetAddress, phySuburb, phyState or no email address.
      if (phyCountry === 'Australia' && (phyState === 'NT' || isStateValid('NT'))) {
        const schema = yup
          .string()
          .required()
          .matches(postCodeRegexNT, 'Invalid postcode')
          .min(4)
          .max(4, 'Invalid postcode');
        return schema.isValidSync(value);
      }

      // Scenario(Non-Complaint): no value for phyState, phyStreetAddress, phySuburb and has email address
      if (!phyState && !phyStreetAddress && !phySuburb && lodgement !== 'Complaint' && isEmailValid) {
        const schema = yup.string();
        return schema.isValidSync(value);
      }

      const schema = yup
        .string()
        .required()
        .max(15, 'Invalid postcode')
        .trim()
        .matches(textAreaRegex, 'Invalid postcode');

      return schema.isValidSync(value);
    }

    return true;
  }),

  postalAddress: yup
    .string()
    .nullable()
    .when(['isManualPosAddress', 'addressType', 'lodgement'], {
      is: (isManualPosAddress: boolean, addressType: string, lodgement: Lodgement) =>
        !isManualPosAddress && addressType === 'no' && lodgement === 'Complaint',
      then: () => yup.string().required(postalAddressErrorMsgMin).min(4, postalAddressErrorMsgMin),
      otherwise: () => yup.string().nullable(),
    }),

  isPosAddressValid: yup.boolean().when(['isManualPosAddress', 'addressType', 'lodgement'], {
    is: (isManualPosAddress: boolean, addressType: string, lodgement: Lodgement) =>
      !isManualPosAddress && addressType === 'no' && lodgement === 'Complaint',
    then: () => yup.boolean().required().oneOf([true]),
  }),

  posStreetAddress: yup.string().when(['isManualPosAddress', 'addressType', 'lodgement'], {
    is: (isManualPosAddress: boolean, addressType: string, lodgement: Lodgement) =>
      isManualPosAddress && addressType === 'no' && lodgement === 'Complaint',
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: addressMax, required: true }),
  }),

  posSuburb: yup.string().when(['isManualPosAddress', 'addressType', 'lodgement'], {
    is: (isManualPosAddress: boolean, addressType: string, lodgement: Lodgement) =>
      isManualPosAddress && addressType === 'no' && lodgement === 'Complaint',
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: addressMax, required: true }),
  }),

  posState: yup.string().when(['isManualPosAddress', 'addressType', 'lodgement'], {
    is: (isManualPosAddress: boolean, addressType: string, lodgement: Lodgement) =>
      isManualPosAddress && addressType === 'no' && lodgement === 'Complaint',
    then: () => validateTextSchema({ minLen: textFieldNameMin, maxLen: stateMax, required: true }),
  }),

  posCountry: yup
    .string()
    .nullable()
    .when(['isManualPosAddress', 'addressType', 'lodgement'], {
      is: (isManualPosAddress: boolean, addressType: string, lodgement: Lodgement) =>
        isManualPosAddress && addressType === 'no' && lodgement === 'Complaint',
      then: () => yup.string().oneOf(countries, 'Country is a required field').required().label('Country'),
    }),

  posPostcode: yup.string().test('postalCode', 'Invalid postcode', function (postCode) {
    const { isManualPosAddress, posState, addressType, lodgement, posCountry } = this.parent;

    if (isManualPosAddress && addressType === 'no' && lodgement === 'Complaint' && typeof postCode === 'string') {
      return validatePostCode(posCountry)(posState)(postCode);
    }

    return true;
  }),

  haveOneValidNumber: yup.boolean().when(['iContactPreferedMethod', 'lodgement'], {
    is: (iContactPreferedMethod: string, lodgement: Lodgement) =>
      iContactPreferedMethod === 'phone' && lodgement === 'Complaint',
    then: () => yup.boolean().oneOf([true]),
    otherwise: () => yup.boolean(),
  }),

  mPhoneNumber: phoneNumberSchema(),
  hPhoneNumber: phoneNumberSchema(),
  oPhoneNumber: phoneNumberSchema(),
  isLangInterpreter: yup.string().when('lodgement', {
    is: 'Complaint',
    then: () =>
      yup
        .string()
        .nullable()
        .oneOf([...YesNoList])
        .required(),
  }),
  language: yup
    .string()
    .nullable()
    .when('isLangInterpreter', {
      is: 'yes',
      then: () =>
        yup
          .string()
          .oneOf([
            'Arabic',
            'Cantonese',
            'Greek',
            'Hindi',
            'Italian',
            'Mandarin',
            'Punjabi',
            'Spanish',
            'Vietnamese',
            'Other',
          ])
          .required(),
    }),

  vulnerableCustomer: yup.string().nullable(),

  otherLanguage: yup
    .string()
    .nullable()
    .when(['isLangInterpreter', 'language'], {
      is: (isLangInterpreter: string, language: string) => isLangInterpreter === 'yes' && language === 'Other',
      then: () => validateTextSchema({ minLen: otherLanguageMin, maxLen: textFieldMax, required: true }),
    }),
};

export const contactReasonSchema = yup.object(contactReasonSchemaObject);
export const contactDetailSchema = yup.object(contactDetailSchemaObject);

export const yourDetailsSchemaObject = {
  ...contactReasonSchemaObject,

  title: yup
    .string()
    .nullable()
    .oneOf([...titleList, null])
    .optional(),

  firstname: validateTextSchema({ minLen: textFieldNameMin, maxLen: textFieldMax, required: true }),
  lastname: validateTextSchema({ minLen: textFieldNameMin, maxLen: textFieldMax, required: true }),

  dob: yup.date().when(['lodgement', 'indOrBus'], {
    is: (lodgement: Lodgement, indOrBus: IndOrBus) => lodgement === 'Complaint' && indOrBus === 'individual',
    then: () => validateDateSchema({ hasMinDate: true }),
    otherwise: () => yup.date().nullable(),
  }),

  gender: yup.string().when(['lodgement', 'indOrBus'], {
    is: (lodgement: Lodgement, indOrBus: IndOrBus) => lodgement === 'Complaint' && indOrBus === 'individual',
    then: () =>
      yup
        .string()
        .oneOf([...genderList])
        .required(),
    otherwise: () => yup.string().nullable(),
  }),

  hasOneValidField: yup.boolean().when(['lodgement'], {
    is: (lodgement: Lodgement) => lodgement !== 'Complaint',
    then: () => yup.boolean().oneOf([true]).required(),
  }),

  isManualPhysAddressValid: yup.boolean().when(['lodgement'], {
    is: (lodgement: Lodgement) => lodgement !== 'Complaint',
    then: () => yup.boolean(),
  }),
};
export const yourDetailsSchema = yup.object(yourDetailsSchemaObject);

const validationSchema = yup.object({
  ...contactReasonSchemaObject,
  ...yourDetailsSchemaObject,
  ...contactDetailSchemaObject,
});

export default validationSchema;
